import React, { useEffect, useState, useRef, useCallback } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
  Plus, Edit, Trash2, X, Briefcase, ToggleLeft, ToggleRight,
  Upload, Image, Star, Eye, AlertTriangle, RotateCcw, Loader2
} from 'lucide-react';
import {
  fetchServices, createService, updateService, deleteService, restoreService,
  uploadServiceImage, deleteImage, setCoverImage,
  fetchCategories, fetchUnits
} from '../../api/supplierApi';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { toast } from 'sonner';
import SecureImage from '../../components/supplier/SecureImage';

const CURRENCIES    = ['USD', 'EUR', 'GBP', 'TRY'];
const PRICING_TYPES = ['fixed', 'hourly', 'daily', 'custom'];
const SEL = "w-full rounded-xl border border-gray-200 px-3 py-2 text-sm bg-white focus:outline-none focus:ring-2 focus:ring-amber-300";
const pLabel = (tr) => ({ fixed: tr ? 'Sabit' : 'Fixed', hourly: tr ? 'Saatlik' : 'Hourly', daily: tr ? 'Günlük' : 'Daily', custom: tr ? 'Özel' : 'Custom' });
const pBadge = { fixed: 'bg-blue-50 text-blue-700', hourly: 'bg-purple-50 text-purple-700', daily: 'bg-emerald-50 text-emerald-700', custom: 'bg-gray-100 text-gray-600' };

// ── Image Manager (reused from ProductsSection) ───────────────────────────────
function ImageManager({ serviceId, images = [], tr, onImagesChange }) {
  const [uploading, setUploading] = useState(false);
  const [lightbox, setLightbox]   = useState(null);
  const inputRef = useRef(null);

  const handleUpload = async (file) => {
    if (!serviceId) { toast.info(tr ? 'Önce hizmeti kaydedin.' : 'Save service first.'); return; }
    if (images.length >= 5) { toast.error(tr ? 'Maks. 5 görsel.' : 'Max 5 images.'); return; }
    if (file.size > 10 * 1024 * 1024) { toast.error(tr ? 'Dosya maks. 10 MB.' : 'File max 10 MB.'); return; }
    setUploading(true);
    try {
      const result = await uploadServiceImage(serviceId, file);
      onImagesChange([...images, result]);
      toast.success(tr ? 'Görsel yüklendi.' : 'Image uploaded.');
    } catch (e) {
      toast.error(e.response?.data?.detail || (tr ? 'Yüklenemedi.' : 'Upload failed.'));
    } finally { setUploading(false); }
  };

  const handleDelete = async (img) => {
    if (!window.confirm(tr ? 'Bu görseli silmek istiyor musunuz?' : 'Delete this image?')) return;
    try {
      await deleteImage(img.id);
      onImagesChange(images.filter(i => i.id !== img.id));
      toast.success(tr ? 'Görsel silindi.' : 'Image deleted.');
    } catch (e) { toast.error(tr ? 'Silinemedi.' : 'Delete failed.'); }
  };

  const handleSetCover = async (img) => {
    try {
      await setCoverImage(img.id);
      onImagesChange(images.map(i => ({ ...i, is_cover: i.id === img.id })));
      toast.success(tr ? 'Kapak güncellendi.' : 'Cover updated.');
    } catch (e) { toast.error(tr ? 'Hata.' : 'Error.'); }
  };

  return (
    <div className="space-y-2">
      <Label className="text-xs font-semibold text-gray-500 uppercase">
        {tr ? `Görseller (${images.length}/5)` : `Images (${images.length}/5)`}
      </Label>
      <div className="flex flex-wrap gap-2">
        {images.map(img => (
          <div key={img.id} className="relative group h-20 w-20 rounded-xl overflow-hidden border-2 border-gray-200 flex-shrink-0">
            <SecureImage src={img.thumb_url || img.url} alt="" className="w-full h-full object-cover" />
            {img.is_cover && <span className="absolute top-0.5 left-0.5 bg-amber-500 text-white rounded text-[9px] px-1 font-bold">{tr ? 'Kapak' : 'Cover'}</span>}
            <div className="absolute inset-0 bg-black/60 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center gap-1">
              <button onClick={() => setLightbox(img)} className="text-white hover:text-amber-300 p-0.5"><Eye className="h-3.5 w-3.5" /></button>
              {!img.is_cover && <button onClick={() => handleSetCover(img)} className="text-white hover:text-amber-300 p-0.5"><Star className="h-3.5 w-3.5" /></button>}
              <button onClick={() => handleDelete(img)} className="text-white hover:text-red-400 p-0.5"><Trash2 className="h-3.5 w-3.5" /></button>
            </div>
          </div>
        ))}
        {images.length < 5 && (
          <>
            <input ref={inputRef} type="file" accept="image/jpeg,image/png,image/webp,image/gif" className="hidden"
              onChange={e => e.target.files?.[0] && handleUpload(e.target.files[0])} />
            <button onClick={() => inputRef.current?.click()} disabled={uploading}
              className="h-20 w-20 rounded-xl border-2 border-dashed border-amber-200 flex items-center justify-center text-amber-400 hover:bg-amber-50 flex-shrink-0">
              {uploading ? <Loader2 className="h-5 w-5 animate-spin" /> : <Upload className="h-5 w-5" />}
            </button>
          </>
        )}
      </div>
      {lightbox && (
        <div className="fixed inset-0 z-[999] bg-black/90 flex items-center justify-center" onClick={() => setLightbox(null)}>
          <button className="absolute top-4 right-4 text-white"><X className="h-8 w-8" /></button>
          <SecureImage src={lightbox.url} alt="" className="max-w-[90vw] max-h-[90vh] object-contain rounded-xl" onClick={e => e.stopPropagation()} />
        </div>
      )}
    </div>
  );
}

function ConfirmModal({ title, message, onConfirm, onCancel, tr }) {
  return (
    <div className="fixed inset-0 z-[60] flex items-center justify-center p-4 bg-black/50 backdrop-blur-sm">
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-sm p-6">
        <div className="flex items-center gap-3 mb-4">
          <div className="h-10 w-10 rounded-full bg-red-100 flex items-center justify-center shrink-0">
            <AlertTriangle className="h-5 w-5 text-red-600" />
          </div>
          <div>
            <h3 className="font-bold text-gray-900 text-sm">{title}</h3>
            <p className="text-xs text-gray-500 mt-0.5">{message}</p>
          </div>
        </div>
        <div className="flex gap-3">
          <Button variant="outline" onClick={onCancel} className="flex-1 rounded-xl text-sm">{tr ? 'İptal' : 'Cancel'}</Button>
          <Button onClick={onConfirm} className="flex-1 rounded-xl bg-red-600 hover:bg-red-700 text-white text-sm">{tr ? 'Sil' : 'Delete'}</Button>
        </div>
      </div>
    </div>
  );
}

// ── Service Modal ─────────────────────────────────────────────────────────────
function ServiceModal({ service, tr, onClose, onSaved, categories, units }) {
  const EMPTY = { title: '', description: '', category: '', main_category_id: '', subcategory_id: '', pricing_type: 'fixed', currency: 'USD', price: '', unit: '', unit_id: '', lead_time_days: '', is_active: true };
  const [form, setForm] = useState(service ? { ...service } : { ...EMPTY });
  const [images, setImages] = useState(service?.image_objects || []);
  const [saving, setSaving] = useState(false);
  const [errors, setErrors] = useState({});
  const isEdit = !!service?.id;
  const set = (k, v) => { setForm(f => ({ ...f, [k]: v })); if (errors[k]) setErrors(e => ({ ...e, [k]: null })); };

  const selectedCat  = categories.find(c => c.id === form.main_category_id);
  const subcategories = selectedCat?.subcategories || [];
  const pl = pLabel(tr);

  const handleCategoryChange = (catId) => {
    const cat = categories.find(c => c.id === catId);
    set('main_category_id', catId);
    set('category', cat?.name || '');
    set('subcategory_id', '');
  };
  const handleUnitChange = (unitId) => {
    const unit = units.find(u => u.id === unitId);
    set('unit_id', unitId);
    set('unit', unit?.code || '');
  };

  const validate = () => {
    const e = {};
    if (!form.title.trim()) e.title = tr ? 'Hizmet adı zorunludur.' : 'Title is required.';
    setErrors(e);
    return Object.keys(e).length === 0;
  };

  const handleSave = async () => {
    if (!validate()) return;
    setSaving(true);
    try {
      const payload = { ...form, price: form.price ? parseFloat(form.price) : null, lead_time_days: form.lead_time_days ? parseInt(form.lead_time_days) : null };
      const saved = isEdit ? await updateService(service.id, payload) : await createService(payload);
      toast.success(tr ? (isEdit ? 'Hizmet güncellendi.' : 'Hizmet eklendi.') : (isEdit ? 'Service updated.' : 'Service added.'));
      onSaved({ ...saved, image_objects: images });
      onClose();
    } catch (e) { toast.error(e.response?.data?.detail || (tr ? 'Hata.' : 'Error.')); }
    finally { setSaving(false); }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50 backdrop-blur-sm" onClick={onClose}>
      <div className="bg-white rounded-3xl shadow-2xl w-full max-w-xl max-h-[92vh] overflow-y-auto" onClick={e => e.stopPropagation()}>
        <div className="flex items-center justify-between px-6 pt-6 pb-4 border-b sticky top-0 bg-white z-10">
          <h3 className="font-bold text-gray-900">{tr ? (isEdit ? 'Hizmeti Düzenle' : 'Hizmet Ekle') : (isEdit ? 'Edit Service' : 'Add Service')}</h3>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600"><X className="h-5 w-5" /></button>
        </div>
        <div className="px-6 py-5 space-y-4">
          {/* Title */}
          <div className="space-y-1.5">
            <Label className="text-xs font-semibold text-gray-500 uppercase">{tr ? 'Hizmet Adı' : 'Title'} <span className="text-red-500">*</span></Label>
            <Input value={form.title} onChange={e => set('title', e.target.value)} className={`rounded-xl ${errors.title ? 'border-red-400' : ''}`} />
            {errors.title && <p className="text-xs text-red-500">{errors.title}</p>}
          </div>

          {/* Category + Subcategory */}
          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-1.5">
              <Label className="text-xs font-semibold text-gray-500 uppercase">{tr ? 'Ana Kategori' : 'Main Category'}</Label>
              <select value={form.main_category_id || ''} onChange={e => handleCategoryChange(e.target.value)} className={SEL}>
                <option value="">{tr ? '-- Kategori --' : '-- Category --'}</option>
                {categories.map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
              </select>
            </div>
            <div className="space-y-1.5">
              <Label className="text-xs font-semibold text-gray-500 uppercase">{tr ? 'Alt Kategori' : 'Subcategory'}</Label>
              <select value={form.subcategory_id || ''} onChange={e => set('subcategory_id', e.target.value)}
                disabled={!form.main_category_id || subcategories.length === 0} className={`${SEL} disabled:bg-gray-50 disabled:text-gray-300`}>
                <option value="">{subcategories.length === 0 ? (tr ? '(yok)' : '(none)') : (tr ? '-- Alt Kategori --' : '-- Subcategory --')}</option>
                {subcategories.map(s => <option key={s.id} value={s.id}>{s.name}</option>)}
              </select>
            </div>
          </div>

          {/* Description */}
          <div className="space-y-1.5">
            <Label className="text-xs font-semibold text-gray-500 uppercase">{tr ? 'Açıklama' : 'Description'}</Label>
            <textarea rows={3} value={form.description || ''} onChange={e => set('description', e.target.value)}
              className="w-full rounded-xl border border-gray-200 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-amber-300 resize-y" />
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-1.5">
              <Label className="text-xs font-semibold text-gray-500 uppercase">{tr ? 'Fiyatlandırma' : 'Pricing Type'}</Label>
              <select value={form.pricing_type} onChange={e => set('pricing_type', e.target.value)} className={SEL}>
                {PRICING_TYPES.map(t => <option key={t} value={t}>{pl[t]}</option>)}
              </select>
            </div>
            <div className="space-y-1.5">
              <Label className="text-xs font-semibold text-gray-500 uppercase">{tr ? 'Para Birimi' : 'Currency'}</Label>
              <select value={form.currency} onChange={e => set('currency', e.target.value)} className={SEL}>
                {CURRENCIES.map(c => <option key={c}>{c}</option>)}
              </select>
            </div>
            {form.pricing_type !== 'custom' && (
              <div className="space-y-1.5">
                <Label className="text-xs font-semibold text-gray-500 uppercase">{tr ? 'Fiyat' : 'Price'}</Label>
                <Input type="number" value={form.price || ''} onChange={e => set('price', e.target.value)} className="rounded-xl" placeholder="0.00" min="0" />
              </div>
            )}
            <div className="space-y-1.5">
              <Label className="text-xs font-semibold text-gray-500 uppercase">{tr ? 'Birim' : 'Unit'}</Label>
              <select value={form.unit_id || ''} onChange={e => handleUnitChange(e.target.value)} className={SEL}>
                <option value="">{tr ? '-- Birim --' : '-- Unit --'}</option>
                {units.map(u => <option key={u.id} value={u.id}>{u.name} ({u.code})</option>)}
              </select>
            </div>
            <div className="space-y-1.5">
              <Label className="text-xs font-semibold text-gray-500 uppercase">{tr ? 'Tedarik Süresi (gün)' : 'Lead Time (days)'}</Label>
              <Input type="number" value={form.lead_time_days || ''} onChange={e => set('lead_time_days', e.target.value)} className="rounded-xl" />
            </div>
            <div className="flex items-end pb-1">
              <label className="flex items-center gap-2 cursor-pointer">
                <button type="button" onClick={() => set('is_active', !form.is_active)}>
                  {form.is_active ? <ToggleRight className="h-7 w-7 text-emerald-500" /> : <ToggleLeft className="h-7 w-7 text-gray-300" />}
                </button>
                <span className="text-sm text-gray-600">{tr ? 'Aktif' : 'Active'}</span>
              </label>
            </div>
          </div>

          {isEdit && <ImageManager serviceId={service.id} images={images} tr={tr} onImagesChange={setImages} />}
          {!isEdit && (
            <p className="text-xs text-amber-600 bg-amber-50 rounded-xl px-3 py-2">
              {tr ? 'Görsel eklemek için önce hizmeti kaydedin.' : 'Save the service first to add images.'}
            </p>
          )}
        </div>
        <div className="px-6 pb-6 flex gap-3 sticky bottom-0 bg-white border-t pt-4">
          <Button onClick={handleSave} disabled={saving} data-testid="service-save-btn"
            className="flex-1 bg-amber-500 hover:bg-amber-600 text-white rounded-xl">
            {saving ? <><Loader2 className="h-4 w-4 mr-2 animate-spin" />{tr ? 'Kaydediliyor...' : 'Saving...'}</> : (tr ? 'Kaydet' : 'Save')}
          </Button>
          <Button variant="outline" onClick={onClose} className="rounded-xl">{tr ? 'İptal' : 'Cancel'}</Button>
        </div>
      </div>
    </div>
  );
}

// ── Main Services Section ─────────────────────────────────────────────────────
export default function ServicesSection({ tr }) {
  const navigate = useNavigate();
  const { slug, subsection, action } = useParams();

  // URL'den modal durumu türetiliyor:
  // /services       → liste (modal yok)
  // /services/new   → yeni hizmet modalı
  // /services/:id/edit → düzenleme modalı
  const basePath = slug
    ? `/tedarikci-admin/${slug}/dashboard/services`
    : '/tedarikci-admin/dashboard/services';

  const openAdd  = () => navigate(`${basePath}/new`);
  const openEdit = (s) => navigate(`${basePath}/${s.id}/edit`);
  const closeModal = () => navigate(basePath);

  const [services, setServices]   = useState([]);
  const [deleted, setDeleted]     = useState([]);
  const [categories, setCategories] = useState([]);
  const [units, setUnits]         = useState([]);
  const [loading, setLoading]     = useState(true);
  const [confirmDelete, setConfirmDelete] = useState(null);
  const [deleting, setDeleting]   = useState(null);
  const [showDeleted, setShowDeleted] = useState(false);
  const [restoring, setRestoring] = useState(null);

  // URL'den modal değeri türet (servis objesi için services yüklendikten sonra çözülüyor)
  const isAddModal  = subsection === 'new';
  const editId      = subsection && subsection !== 'new' && action === 'edit' ? subsection : null;
  const editService = editId ? (services.find(s => s.id === editId) || null) : null;
  const modal       = isAddModal ? 'add' : (editService || null);

  const load = useCallback(() => {
    setLoading(true);
    Promise.all([
      fetchServices(),
      fetchServices({ include_deleted: true }).catch(() => []),
      fetchCategories().catch(() => []),
      fetchUnits().catch(() => []),
    ]).then(([svcs, all, cats, unitList]) => {
      setServices(svcs);
      setDeleted(Array.isArray(all) ? all.filter(s => s.deleted_at) : []);
      setCategories(cats);
      setUnits(unitList);
    }).catch(() => toast.error(tr ? 'Hizmetler yüklenemedi.' : 'Failed to load services.'))
      .finally(() => setLoading(false));
  }, [tr]);

  useEffect(() => { load(); }, [load]);

  const handleSaved = (saved) => {
    setServices(prev => { const i = prev.findIndex(s => s.id === saved.id); return i >= 0 ? prev.map(s => s.id === saved.id ? saved : s) : [saved, ...prev]; });
    closeModal();
  };

  const handleDelete = async () => {
    if (!confirmDelete) return;
    setDeleting(confirmDelete.id);
    setConfirmDelete(null);
    try {
      await deleteService(confirmDelete.id);
      const removed = services.find(s => s.id === confirmDelete.id);
      setServices(prev => prev.filter(s => s.id !== confirmDelete.id));
      if (removed) setDeleted(prev => [{ ...removed, deleted_at: new Date().toISOString() }, ...prev]);
      toast.success(tr ? 'Hizmet çöp kutusuna taşındı.' : 'Service moved to trash.');
    } catch (e) { toast.error(e.response?.data?.detail || (tr ? 'Silinemedi.' : 'Delete failed.')); }
    finally { setDeleting(null); }
  };

  const handleRestore = async (s) => {
    setRestoring(s.id);
    try {
      await restoreService(s.id);
      setDeleted(prev => prev.filter(d => d.id !== s.id));
      setServices(prev => [{ ...s, deleted_at: null }, ...prev]);
      toast.success(tr ? 'Hizmet geri alındı.' : 'Service restored.');
    } catch { toast.error(tr ? 'Geri alınamadı.' : 'Restore failed.'); }
    finally { setRestoring(null); }
  };

  const displayList = showDeleted ? deleted : services;

  const pl = pLabel(tr);

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-2xl font-bold text-gray-900">{tr ? 'Hizmetlerim' : 'My Services'}</h2>
          <p className="text-sm text-gray-500 mt-0.5">{services.length} {tr ? 'aktif' : 'active'}{deleted.length > 0 ? ` · ${deleted.length} ${tr ? 'silinmiş' : 'deleted'}` : ''}</p>
        </div>
        <div className="flex gap-2">
          {deleted.length > 0 && (
            <Button variant="outline" size="sm" onClick={() => setShowDeleted(!showDeleted)}
              className={`rounded-xl text-xs ${showDeleted ? 'border-red-300 text-red-600 bg-red-50' : 'border-gray-200 text-gray-500'}`}>
              <RotateCcw className="h-3.5 w-3.5 mr-1.5" />
              {showDeleted ? (tr ? 'Aktif Görünüm' : 'Active View') : `${tr ? 'Çöp Kutusu' : 'Trash'} (${deleted.length})`}
            </Button>
          )}
          {!showDeleted && (
            <Button onClick={openAdd} data-testid="add-service-btn"
              className="bg-amber-500 hover:bg-amber-600 text-white rounded-xl">
              <Plus className="h-4 w-4 mr-2" />{tr ? 'Hizmet Ekle' : 'Add Service'}
            </Button>
          )}
        </div>
      </div>

      {loading ? (
        <div className="flex justify-center h-48 items-center">
          <div className="animate-spin h-10 w-10 border-4 border-amber-500 border-t-transparent rounded-full" />
        </div>
      ) : displayList.length === 0 ? (
        <div className="flex flex-col items-center h-48 justify-center text-center bg-white rounded-2xl border border-gray-100">
          <Briefcase className="h-12 w-12 text-gray-200 mb-3" />
          <p className="text-gray-500 font-medium">
            {showDeleted ? (tr ? 'Çöp kutusunda hizmet yok.' : 'No deleted services.') : (tr ? 'Henüz hizmet eklenmedi.' : 'No services added yet.')}
          </p>
          {!showDeleted && <Button size="sm" onClick={openAdd} className="mt-4 bg-amber-500 text-white rounded-xl">{tr ? 'İlk hizmeti ekle' : 'Add first service'}</Button>}
        </div>
      ) : (
        <div className="grid md:grid-cols-2 gap-4">
          {displayList.map(s => {
            const cover = s.image_objects?.find(i => i.is_cover) || s.image_objects?.[0];
            const isDeleted = !!s.deleted_at;
            return (
              <div key={s.id} className={`bg-white rounded-2xl shadow-sm border overflow-hidden ${isDeleted ? 'border-red-100 opacity-75' : s.is_active ? 'border-gray-100' : 'border-gray-200 opacity-60'}`}>
                {cover?.thumb_url && (
                  <div className="h-32 bg-gray-50">
                    <SecureImage src={cover.thumb_url} alt={s.title} className="w-full h-full object-cover" />
                  </div>
                )}
                <div className="p-5">
                  <div className="flex items-start justify-between gap-2 mb-2">
                    <div className="flex-1 min-w-0">
                      <h4 className="font-semibold text-gray-900 text-sm">{s.title}</h4>
                      {s.category && <span className="text-xs text-amber-700 bg-amber-50 px-2 py-0.5 rounded-full mt-1 inline-block">{s.category}</span>}
                      {isDeleted && <span className="text-xs text-red-600 bg-red-50 px-2 py-0.5 rounded-full ml-1">{tr ? 'Silindi' : 'Deleted'}</span>}
                    </div>
                    <div className="flex gap-1">
                      {isDeleted ? (
                        <Button size="sm" variant="ghost" onClick={() => handleRestore(s)} disabled={restoring === s.id}
                          className="h-8 px-3 text-emerald-600 hover:bg-emerald-50 rounded-lg text-xs font-medium">
                          {restoring === s.id ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <><RotateCcw className="h-3.5 w-3.5 mr-1" />{tr ? 'Geri Al' : 'Restore'}</>}
                        </Button>
                      ) : (
                        <>
                          <Button size="sm" variant="ghost" onClick={() => openEdit(s)} className="h-8 w-8 p-0 text-blue-500 hover:bg-blue-50 rounded-lg"><Edit className="h-3.5 w-3.5" /></Button>
                          <Button size="sm" variant="ghost" onClick={() => setConfirmDelete(s)} disabled={deleting === s.id} className="h-8 w-8 p-0 text-red-500 hover:bg-red-50 rounded-lg">
                            {deleting === s.id ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Trash2 className="h-3.5 w-3.5" />}
                          </Button>
                        </>
                      )}
                    </div>
                  </div>
                  {s.description && <p className="text-xs text-gray-500 line-clamp-2 mb-3">{s.description}</p>}
                  <div className="flex items-center gap-2 flex-wrap">
                    <span className={`text-xs px-2 py-0.5 rounded-full font-medium ${pBadge[s.pricing_type] || 'bg-gray-100 text-gray-600'}`}>{pl[s.pricing_type] || s.pricing_type}</span>
                    {s.price != null && s.pricing_type !== 'custom' && <span className="text-sm font-bold text-amber-600">{s.currency} {parseFloat(s.price).toFixed(2)}</span>}
                    {s.unit && <span className="text-xs text-gray-400">/ {s.unit}</span>}
                    {s.lead_time_days && <span className="text-xs text-gray-400">{s.lead_time_days}{tr ? 'g' : 'd'}</span>}
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}

      {modal && <ServiceModal service={modal === 'add' ? null : modal} tr={tr} onClose={closeModal} onSaved={handleSaved} categories={categories} units={units} />}
      {confirmDelete && (
        <ConfirmModal
          title={tr ? 'Hizmeti Sil' : 'Delete Service'}
          message={tr ? `"${confirmDelete.title}" kalıcı olarak silinecek.` : `"${confirmDelete.title}" will be permanently deleted.`}
          onConfirm={handleDelete}
          onCancel={() => setConfirmDelete(null)}
          tr={tr}
        />
      )}
    </div>
  );
}
